import { FormField } from "@/v2/components/form/form-schema";
import {
  FieldFilter,
  isBetweenFilter,
  isGreaterThanOrEqualFilter,
  isLessThanOrEqualFilter,
  isNumericBound,
} from "@/v2/components/filter-menu/FieldFilter.model";
import {
  FilterMenuItemOption,
  FilterMenuItemType,
  FilterMenuType,
} from "@/v2/components/filter-menu/FilterMenu.model";
import {
  calendarDateToMs,
  msToCalendarDate,
} from "@/v2/components/filter-menu/dateBoundValue";
import {
  EscalationCustomFieldFilter,
  EscalationCustomFieldFilters,
} from "@/v2/entities/escalation-rule/EscalationRule.model";

export type ConditionFiltersValue = Partial<Record<string, FieldFilter>>;

const EPOCH_DATE_FIELD_IDS = new Set(["eventStartDate", "eventEndDate"]);

function stripCompositeKeySuffix(fieldId: string): string {
  return fieldId.replace(/::(?:gte|lte)$/, "");
}

function isEpochDateField(fieldId: string): boolean {
  return EPOCH_DATE_FIELD_IDS.has(stripCompositeKeySuffix(fieldId));
}

function compositeKeyForFilter(baseField: string, filter: FieldFilter): string {
  if (!isEpochDateField(baseField)) return baseField;
  if (isGreaterThanOrEqualFilter(filter)) return `${baseField}::gte`;
  if (isLessThanOrEqualFilter(filter)) return `${baseField}::lte`;
  return baseField;
}

/**
 * Ensures the composite key suffix (e.g. `::gte`, `::lte`) matches the
 * operator inside each filter value.
 */
export function fixCompositeKeys(
  filters: ConditionFiltersValue
): ConditionFiltersValue {
  const fixed: ConditionFiltersValue = {};
  for (const [key, filter] of Object.entries(filters)) {
    if (filter == null) continue;
    const base = stripCompositeKeySuffix(key);
    const correctKey = compositeKeyForFilter(base, filter);
    fixed[correctKey] = filter;
  }
  return fixed;
}

function isValidStoredValue(value: string): boolean {
  return value !== "" && value !== "undefined";
}

// NaN for an unparseable value, so the caller falls through to its generic handling
function endOfStoredDay(ms: number): number {
  const date = msToCalendarDate(ms);
  return date != null ? calendarDateToMs(date, "end") : NaN;
}

function stripLikeWildcards(value: string): string {
  return value.replaceAll("%", "");
}

function toSelectOptions(
  options: { value: string | number; label: string }[]
): FilterMenuItemOption[] {
  return options
    .filter((o) => o != null && isValidStoredValue(String(o.value)))
    .map((o) => ({
      label:
        o.label != null && isValidStoredValue(String(o.label))
          ? String(o.label)
          : String(o.value),
      value: String(o.value),
    }));
}

function customFieldLabel(field: FormField): string {
  const label =
    field.label != null && isValidStoredValue(String(field.label))
      ? field.label
      : field.id;
  return `Custom Field: ${label}`;
}

export function isSupportedEscalationConditionField(field: FormField): boolean {
  if (field.id == null || !isValidStoredValue(String(field.id))) {
    return false;
  }

  if (
    field.kind === "boolean" ||
    field.kind === "number" ||
    field.kind === "text"
  ) {
    return true;
  }

  if (
    (field.kind === "select" || field.kind === "radio") &&
    "options" in field &&
    Array.isArray(field.options) &&
    field.options.length > 0
  ) {
    return true;
  }

  if (field.kind === "array") {
    const of = field.of;
    if (of.kind === "boolean" || of.kind === "number" || of.kind === "text") {
      return true;
    }
    if (
      (of.kind === "select" || of.kind === "radio") &&
      "options" in of &&
      Array.isArray(of.options) &&
      of.options.length > 0
    ) {
      return true;
    }
  }

  return false;
}

export function buildCustomFieldMenuItems(
  field: FormField
): FilterMenuItemType<string>[] {
  if (!isSupportedEscalationConditionField(field)) {
    return [];
  }

  const label = customFieldLabel(field);

  if (field.kind === "boolean") {
    return [{
      field: field.id,
      kind: "boolean",
      label,
    }];
  }

  if (isEpochDateField(field.id)) {
    return [{
      field: field.id,
      kind: "dateRange",
      label,
    }];
  }

  if (field.kind === "number" || field.kind === "text") {
    return [{
      field: field.id,
      kind: "search",
      label,
      placeholder: "Enter exact value",
    }];
  }

  if (field.kind === "select" || field.kind === "radio") {
    const options = toSelectOptions(field.options);
    if (options.length === 0) return [];
    return [{
      field: field.id,
      kind: "single",
      label,
      options,
      disableOperator: true,
    }];
  }

  if (field.kind === "array") {
    const of = field.of;
    if (of.kind === "boolean") {
      return [{
        field: field.id,
        kind: "boolean",
        label,
      }];
    }
    if (of.kind === "select" || of.kind === "radio") {
      const options = toSelectOptions(of.options);
      if (options.length === 0) return [];
      return [{
        field: field.id,
        kind: "single",
        label,
        options,
        disableOperator: true,
      }];
    }
    if (of.kind === "number" || of.kind === "text") {
      return [{
        field: field.id,
        kind: "search",
        label,
        placeholder: "Enter exact value",
      }];
    }
  }

  return [];
}

export function buildEscalationConditionMenu(params: {
  topics: { topic: string }[];
  customFields: FormField[];
}): FilterMenuType<string> {
  const items: FilterMenuType<string> = [];

  const topicOptions = params.topics.filter(
    (t) => t.topic != null && isValidStoredValue(String(t.topic))
  );
  if (topicOptions.length > 0) {
    items.push({
      field: "topic",
      kind: "multi",
      label: "Topic",
      options: topicOptions.map((t) => ({
        label: t.topic,
        value: t.topic,
      })),
      keepOpenOnSelect: true,
      disableOperator: true,
    });
  }

  for (const field of params.customFields) {
    items.push(...buildCustomFieldMenuItems(field));
  }

  return items;
}

export function filterValueToStoredFilter(
  filter: FieldFilter
): EscalationCustomFieldFilter | undefined {
  if (isBetweenFilter(filter)) {
    const [from, to] = filter.value;
    return from <= to
      ? { value: String(from), valueTo: String(to) }
      : undefined;
  }

  if (isGreaterThanOrEqualFilter(filter)) {
    return { value: String(filter.value), operator: "gte" };
  }

  if (isLessThanOrEqualFilter(filter)) {
    return { value: String(filter.value), operator: "lte" };
  }

  if (!("value" in filter)) return undefined;

  const value = filter.value;

  if (typeof value === "boolean") {
    if (filter.operator === "eq") return { value: String(value) };
    if (filter.operator === "ne") return { value: String(!value) };
    return undefined;
  }

  if (filter.operator !== "eq" && filter.operator !== "like") {
    return undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return { value: String(value) };
  }
  if (typeof value === "string") {
    const stripped = stripLikeWildcards(value);
    return isValidStoredValue(stripped) ? { value: stripped } : undefined;
  }

  return undefined;
}

export function storedValueToFieldFilter(
  fieldId: string,
  field: FormField | undefined,
  storedFilter: EscalationCustomFieldFilter
): FieldFilter | undefined {
  const stored = storedFilter.value;
  if (!isValidStoredValue(stored)) return undefined;

  // explicit operator from the DB — after / before
  if (isEpochDateField(fieldId) && storedFilter.operator === "gte") {
    const val = Number(stored);
    return isNumericBound(val) ? { operator: "gte", value: val } : undefined;
  }
  if (isEpochDateField(fieldId) && storedFilter.operator === "lte") {
    const val = Number(stored);
    return isNumericBound(val) ? { operator: "lte", value: val } : undefined;
  }

  const rawTo = storedFilter.valueTo;
  const from = Number(stored);
  // a bound-less row predates ranges: its bare value came from a whole-day picker, so widen to
  // the end of that day — the same bounds a single-day pick produces
  const to =
    rawTo != null && isValidStoredValue(rawTo)
      ? Number(rawTo)
      : endOfStoredDay(from);

  // resolved by field id, so a saved range still renders while the field metadata is loading
  if (
    isEpochDateField(fieldId) &&
    Number.isFinite(from) &&
    Number.isFinite(to) &&
    from <= to
  ) {
    return { operator: "between", value: [from, to] };
  }

  if (
    field?.kind === "boolean" ||
    (field?.kind === "array" && field.of.kind === "boolean")
  ) {
    if (stored === "true") return { operator: "eq", value: true };
    if (stored === "false") return { operator: "eq", value: false };
    return undefined;
  }

  if (
    field?.kind === "select" ||
    field?.kind === "radio" ||
    (field?.kind === "array" &&
      (field.of.kind === "select" || field.of.kind === "radio"))
  ) {
    return { operator: "eq", value: stored };
  }

  if (
    field?.kind === "number" ||
    field?.kind === "text" ||
    field?.kind === "array" ||
    field == null
  ) {
    return { operator: "like", value: stored };
  }

  return undefined;
}

export function buildFiltersFromValue(params: {
  topics: string[];
  customFieldFilters?: EscalationCustomFieldFilters;
  customFields: FormField[];
}): ConditionFiltersValue {
  const filters: ConditionFiltersValue = {};
  const validTopics = (params.topics ?? []).filter(isValidStoredValue);
  if (validTopics.length > 0) {
    filters.topic = { operator: "in", value: validTopics };
  }

  const fieldsById = new Map(
    params.customFields.map((field) => [field.id, field])
  );
  const custom = params.customFieldFilters ?? {};
  for (const [compositeKey, stored] of Object.entries(custom)) {
    const baseFieldId = stripCompositeKeySuffix(compositeKey);
    const filter = storedValueToFieldFilter(
      compositeKey,
      fieldsById.get(baseFieldId),
      stored
    );
    if (filter) {
      filters[compositeKey] = filter;
    }
  }

  return filters;
}

export function parseFiltersToFormValue(
  filters: ConditionFiltersValue
): {
  topics: string[];
  customFieldFilters: EscalationCustomFieldFilters | undefined;
} {
  const topics: string[] = [];
  if (
    filters.topic &&
    "value" in filters.topic &&
    Array.isArray(filters.topic.value)
  ) {
    filters.topic.value.forEach((t) => {
      if (typeof t === "string" && isValidStoredValue(t)) topics.push(t);
    });
  }

  const customFieldFilters: EscalationCustomFieldFilters = {};
  for (const [key, filter] of Object.entries(filters)) {
    if (key === "topic" || filter == null) continue;
    const stored = filterValueToStoredFilter(filter);
    if (stored != null) {
      customFieldFilters[key] = stored;
    }
  }

  return {
    topics,
    customFieldFilters:
      Object.keys(customFieldFilters).length > 0
        ? customFieldFilters
        : undefined,
  };
}

/**
 * Fills in the upper bound a pre-range row never stored, so a save that never touched the
 * conditions still writes the range the menu is showing. Non-range fields pass through untouched.
 */
export function normalizeStoredCustomFieldFilters(
  customFieldFilters: EscalationCustomFieldFilters
): EscalationCustomFieldFilters {
  const normalized: EscalationCustomFieldFilters = {};
  for (const [fieldId, stored] of Object.entries(customFieldFilters)) {
    const filter = storedValueToFieldFilter(fieldId, undefined, stored);
    normalized[fieldId] =
      filter != null && isBetweenFilter(filter)
        ? filterValueToStoredFilter(filter) ?? stored
        : stored;
  }
  return normalized;
}
